/**
 * Chamadas da tela pública de assinatura (`/assinatura/:token`) — sem
 * sessão. Usa `fetch` cru, como `authApi` em `shared/lib/api.ts`: o
 * axios compartilhado (`shared/lib/axios`) tem interceptor de refresh de
 * sessão que não faz sentido para um visitante anônimo.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export interface SignaturePreview {
  contractCode: string;
  signatoryName: string;
  status: 'pending' | 'signed' | 'rejected';
  documentUrl: string | null;
}

async function parse<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const message = body?.message;
    throw new Error(Array.isArray(message) ? message.join('. ') : message || 'Falha na requisição.');
  }
  return (body?.data ?? body) as T;
}

export const publicSigningApi = {
  async getPreview(token: string): Promise<SignaturePreview> {
    const response = await fetch(`${BASE_URL}/public/contract-signatures/${token}`);
    return parse<SignaturePreview>(response);
  },

  async requestCode(token: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/public/contract-signatures/${token}/request-code`, {
      method: 'POST',
    });
    await parse<void>(response);
  },

  async confirm(token: string, code: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/public/contract-signatures/${token}/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    await parse<void>(response);
  },

  async reject(token: string, reason: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/public/contract-signatures/${token}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    await parse<void>(response);
  },
};
